<!-- @format -->

<template>
	<div>
		<CoreDropdown
			v-model="relation"
			:options="typeOptions"
			:placeholder="$t('§ui.placeholder_select_file_type')"
			:disabled="!isOptionalFileType"
			class="mb-5"
		/>

		<CoreTwoColumnsRow
			v-if="collectTypeMetadata && isTakeoverProtocolType"
			class="mb-5"
		>
			<template #left>
				<CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-50">
					{{ $t('§contracts.takeover_date') }}
				</CoreBorderWrapper>
			</template>
			<template #right>
				<CoreDateInput
					v-model="begin"
					class="flex-grow-1 flex-shrink-1 flex-basis-50"
				/>
			</template>
		</CoreTwoColumnsRow>

		<CoreFileUpload
			class="mb-5"
			name="file"
			accept=".jpg,.jpeg,.png,.pdf,.xls,.xlsx,.doc,.docx"
			ref="fileUpload"
			:disabled="isUploadDisabled"
			:metadata="getUploadMetadata()"
			:uploadUrl="uploadUrl"
			:acceptJson="true"
			@file-added="handleFileAdded"
			@uploaded="handleSuccess($event)"
			@upload-error="handleUploadError"
		/>

		<CoreTwoColumnsRow
			v-if="isExpiryDateInputShown"
			class="mb-5"
		>
			<template #left>
				<CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-50">
					{{ $t('§contracts.expiry_date') }}
				</CoreBorderWrapper>
			</template>
			<template #right>
				<CoreDateInput
					v-model="expiry_date"
					class="flex-grow-1 flex-shrink-1 flex-basis-50"
				/>
			</template>
		</CoreTwoColumnsRow>

		<CoreTwoColumnsRow
			v-if="isEquipmentPurchaseContractDueDateInputShown"
			class="mb-5"
		>
			<template #left>
				<CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-50">
					{{ $t('§contracts.equipment_purchase_contract_due_date') }}
				</CoreBorderWrapper>
			</template>
			<template #right>
				<CoreDateInput
					v-model="equipment_purchase_contract_due_date"
					class="flex-grow-1 flex-shrink-1 flex-basis-50"
				/>
			</template>
		</CoreTwoColumnsRow>

		<CoreTwoColumnsRow
			v-if="isEquipmentPurchaseContractDueDateInputShown"
			class="mb-5"
		>
			<template #left>
				<CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-50">
					{{ $t('§contracts.equipment_purchase_contract_oem_purchase_order_number') }}
				</CoreBorderWrapper>
			</template>
			<template #right>
				<CoreStringInput
					v-model="oem_purchase_order_number"
					class="flex-grow-1 flex-shrink-1 flex-basis-50"
					placeholder=""
				/>
			</template>
		</CoreTwoColumnsRow>

		<CoreTwoColumnsRow
			v-if="isPolicyHolderInputsShown"
			class="mb-5"
		>
			<template #left>
				<CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-50">
					{{ $t('§contracts.policy_holder') }}
				</CoreBorderWrapper>
			</template>
			<template #right>
				<CoreDropdown
					v-model="policy_holder"
					:options="policyHolderOptions"
					class="flex-grow-1 flex-shrink-1 flex-basis-50"
				/>
			</template>
		</CoreTwoColumnsRow>

		<CoreTwoColumnsRow
			v-if="isPolicyHolderInputsShown"
			class="mb-5"
		>
			<template #left>
				<CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-50">
					{{ $t('§contracts.policy_id') }}
				</CoreBorderWrapper>
			</template>
			<template #right>
				<CoreStringInput
					v-model="policy_id"
					class="flex-grow-1 flex-shrink-1 flex-basis-50"
					placeholder=""
				/>
			</template>
		</CoreTwoColumnsRow>

		<div class="flex justify-start gap-2">
			<CoreButton
				:disabled="isUploadButtonDisabled"
				label="§ui_button.upload"
				icon="pi-upload"
				:loading="saving"
				@click="handleUpload"
			/>

			<CoreButton
				label="§ui_button.cancel"
				variant="tertiary"
				:disabled="saving"
				@click="handleClose"
				icon="pi-times"
			/>
		</div>
	</div>
</template>

<script>
import { parseISO, isValid, format, addDays } from 'date-fns'
import { useModalStore } from '@/stores/modal'
import { useContractStore } from '@/stores/contract'
import { useContractFilesStore } from '@/stores/contractFiles'
import CoreBorderWrapper from '@/components/core/CoreBorderWrapper.vue'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import CoreDateInput from '@/components/core/CoreDateInput.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreFileUpload from '@/components/core/CoreFileUpload.vue'
import CoreStringInput from '@/components/core/CoreStringInput.vue'

export default {
	name: 'UploadContractFileModal',

	components: {
		CoreBorderWrapper,
		CoreTwoColumnsRow,
		CoreDropdown,
		CoreDateInput,
		CoreButton,
		CoreFileUpload,
		CoreStringInput,
	},

	data() {
		return {
			saving: false,
			fileId: null,
			refreshContract: false,
			error: '',
			relation: '',
			isFileSelected: false,
			begin: null,
			expiry_date: null,
			policy_holder: 1,
			policy_id: '',
			equipment_purchase_contract_due_date: addDays(new Date(), 10),
			oem_purchase_order_number: '',
		}
	},

	computed: {
		isOptionalFileType() {
			return !this.modalStore.data.group
		},

		typeOptions() {
			let fileNames
			const uploadableTypes = this.modalStore.data.uploadableTypes
			if (Array.isArray(uploadableTypes)) {
				fileNames = [...uploadableTypes]
			} else if (this.isOptionalFileType) {
				fileNames = [...this.contractFilesStore.optionalTypes]
			} else {
				const group = this.modalStore.data.group
				if (group === 'internalFiles') {
					fileNames = [...this.contractFilesStore.internalTypes]
				} else if (group === 'maintenanceProtocolFiles') {
					const warrantyCovered = this.contractFilesStore.maintenanceProtocolWarrantyCovered ?? {}
					fileNames = Object.keys(this.contractFilesStore.maintenanceProtocolFiles).filter(
						(name) => name === this.modalStore.data.relation || !(name in warrantyCovered),
					)
				} else {
					fileNames = [
						...this.contractFilesStore.publicTypes,
						...this.contractFilesStore.insuranceTypes,
					]
				}
			}

			return fileNames.map((name) => {
				const match = name.match(/^maintenance_protocol_y(\d+)$/)
				const label = match
					? `${this.$t('§contracts.maintenance_protocol_year')} ${match[1]}`
					: this.$t(`§contracts.file_type_${name}`)
				return { label, value: name }
			})
		},

		policyHolderOptions() {
			const insurancePolicyholderList =
				this.contractStore.contractData.insurance_policyholder_list || {}

			return Object.entries(insurancePolicyholderList).map(([id, name]) => ({
				label: name,
				value: parseInt(id),
			}))
		},

		isUploadDisabled() {
			if (this.relation && this.relation.length > 0) {
				return false
			}

			return true
		},

		isTakeoverProtocolType() {
			return this.relation === 'takeover_protocol'
		},

		isUploadButtonDisabled() {
			if (this.isTakeoverProtocolType) {
				return !this.isFileSelected || !this.begin
			}

			if (this.isExpiryDateInputShown) {
				return !this.isFileSelected || !this.expiry_date
			}

			if (this.isPolicyHolderInputsShown) {
				return !this.isFileSelected || !this.policy_id || this.policy_id.length < 5
			}

			if (this.isEquipmentPurchaseContractDueDateInputShown) {
				return (
					!this.isFileSelected ||
					!this.equipment_purchase_contract_due_date ||
					!this.oem_purchase_order_number
				)
			}

			return !this.isFileSelected
		},

		isExpiryDateInputShown() {
			const TYPES_WITH_EXPIRY_DATE_INPUT = [
				'insurance_certificate_general_insurance',
				'insurance_certificate_machine_breakdown',
			]

			return this.collectTypeMetadata && TYPES_WITH_EXPIRY_DATE_INPUT.includes(this.relation)
		},

		isEquipmentPurchaseContractDueDateInputShown() {
			return this.collectTypeMetadata && this.relation === 'equipment_purchase_contract'
		},

		isPolicyHolderInputsShown() {
			const TYPES_WITH_POLICY_HOLDER_INPUTS = [
				'insurance_policy_general_insurance',
				'insurance_policy_machine_breakdown',
			]

			return (
				this.collectTypeMetadata && TYPES_WITH_POLICY_HOLDER_INPUTS.includes(this.relation)
			)
		},

		collectTypeMetadata() {
			return this.modalStore.data.collectTypeMetadata ?? true
		},

		uploadRouteName() {
			return this.modalStore.data.uploadRouteName ?? 'contract.upload'
		},

		uploadUrl() {
			return this.route(this.uploadRouteName, [this.modalStore.data.contractId])
		},
	},

	created() {
		this.modalStore = useModalStore()
		this.contractStore = useContractStore()
		this.contractFilesStore = useContractFilesStore()
	},

	mounted() {
		if (this.modalStore.data.relation !== undefined) {
			this.relation = this.modalStore.data.relation
		}

		if (this.modalStore.data.refreshContract !== undefined) {
			this.refreshContract = this.modalStore.data.refreshContract
		}

		if (this.isTakeoverProtocolType) {
			const beginDateObject = parseISO(this.contractStore.contractData.begin)
			if (isValid(beginDateObject)) {
				this.begin = beginDateObject
			}
		}

		if (this.contractStore.contractData?.equipment_purchase_contract_oem_purchase_order_number) {
			this.oem_purchase_order_number =
				this.contractStore.contractData.equipment_purchase_contract_oem_purchase_order_number
		}
	},

	methods: {
		handleFileAdded() {
			this.isFileSelected = true
		},

		handleClose() {
			this.modalStore.setClose()
		},

		handleUpload() {
			this.isFileSelected = false

			if (!this.saving && this.$refs.fileUpload) {
				this.saving = true
				this.$refs.fileUpload.upload()
			}
		},

		handleUploadError() {
			this.saving = false
			this.isFileSelected = false
		},

		handleSuccess(e) {
			try {
				const onUploaded = this.modalStore.data.onUploaded
				if (typeof onUploaded === 'function') {
					onUploaded()
				} else {
					if (!this.refreshContract && !this.isOptionalFileType) {
						const response = JSON.parse(e.response)
						const group = this.modalStore.data.group

						if (group === 'internalFiles') {
							this.contractFilesStore.addFileToInternalGroup(response.file)
						} else if (group === 'maintenanceProtocolFiles') {
							this.contractFilesStore.addFileToMaintenanceProtocolGroup(response.file)
						} else {
							this.contractFilesStore.addFileToPublicGroup(response.file)
						}
					}

					if (!this.refreshContract && this.isOptionalFileType) {
						this.contractFilesStore.optionalFilesTableRefreshKey += 1
					}

					if (this.refreshContract) {
						this.contractFilesStore.missingContractDocumentsTableRefreshKey += 1
					}
				}
			} catch (error) {
				this.error = error.message || 'An error occurred while processing the file upload.'
				console.error(this.error)
				this.$toast.add({
					severity: 'error',
					detail: this.error,
					life: 1000,
				})
			} finally {
				this.saving = false
				this.isFileSelected = false
				this.handleClose()
			}
		},

		handleUploadError() {
			this.saving = false
			this.isFileSelected = false
		},

		getUploadMetadata() {
			const metadata = {
				relation: this.relation,
			}

			if (this.isTakeoverProtocolType && this.begin) {
				metadata.begin = format(this.begin, 'y-MM-dd')
			}

			if (this.isExpiryDateInputShown && this.expiry_date) {
				metadata.expiry_date = format(this.expiry_date, 'y-MM-dd')
			}

			if (this.isPolicyHolderInputsShown) {
				metadata.policy_holder = this.policy_holder
				metadata.policy_id = this.policy_id
			}

			if (
				this.isEquipmentPurchaseContractDueDateInputShown &&
				this.equipment_purchase_contract_due_date
			) {
				metadata.equipment_purchase_contract_due_date = format(
					this.equipment_purchase_contract_due_date,
					'y-MM-dd',
				)
				metadata.equipment_purchase_contract_oem_purchase_order_number =
					this.oem_purchase_order_number
			}

			return metadata
		},
	},
}
</script>
